import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getAuditLogs } from '../../../services/auditService';
import { useAuth } from '../../../contexts/AuthContext';
import { exportAuditLogsToCSV, exportAuditLogsToPDF } from '../../../services/exportService';
import { logAction } from '../../../services/auditService';

const AuditLog = () => {
  const { userProfile } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const filteredLogs = auditLogs?.filter((log) => {
    const matchesSearch =
      log?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      log?.userName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      log?.userEmail?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      log?.entityName?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesSearch;
  })?.sort((a, b) => {
    const dateA = new Date(a?.timestamp);
    const dateB = new Date(b?.timestamp);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  useEffect(() => {
    loadAuditLogs();
  }, [filterAction, filterEntity, dateRange]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const filters = {
        actionType: filterAction,
        entityType: filterEntity,
        searchTerm: searchTerm,
        startDate: dateRange?.start ? new Date(dateRange?.start) : null,
        endDate: dateRange?.end ? new Date(dateRange?.end) : null,
      };
      const logs = await getAuditLogs(filters);
      setAuditLogs(logs || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadAuditLogs();
  };

  const handleExportCSV = async () => {
    try {
      const result = exportAuditLogsToCSV(filteredLogs);
      if (result?.success) {
        // Log the export action
        await logAction({
          actionType: 'export',
          entityType: 'report',
          entityName: 'Audit Logs',
          description: `Exported ${filteredLogs?.length} audit log records to CSV`,
          changesAfter: { format: 'CSV', recordCount: filteredLogs?.length }
        });
      }
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Failed to export audit logs to CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      const result = await exportAuditLogsToPDF(filteredLogs);
      if (result?.success) {
        // Log the export action
        await logAction({
          actionType: 'export',
          entityType: 'report',
          entityName: 'Audit Logs',
          description: `Exported ${filteredLogs?.length} audit log records to PDF`,
          changesAfter: { format: 'PDF', recordCount: filteredLogs?.length }
        });
      }
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export audit logs to PDF');
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create':
        return 'text-success bg-success/10';
      case 'update':
        return 'text-blue-600 bg-blue-500/10';
      case 'delete':
        return 'text-destructive bg-destructive/10';
      case 'login':
        return 'text-green-600 bg-green-500/10';
      case 'logout':
        return 'text-gray-600 bg-gray-500/10';
      case 'export':
        return 'text-purple-600 bg-purple-500/10';
      case 'view':
        return 'text-blue-500 bg-blue-400/10';
      case 'permission_change':
        return 'text-warning bg-warning/10';
      case 'status_change':
        return 'text-orange-600 bg-orange-500/10';
      case 'data_refresh':
        return 'text-cyan-600 bg-cyan-500/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create':
        return 'Plus';
      case 'update':
        return 'Edit';
      case 'delete':
        return 'Trash2';
      case 'login':
        return 'LogIn';
      case 'logout':
        return 'LogOut';
      case 'export':
        return 'Download';
      case 'view':
        return 'Eye';
      case 'permission_change':
        return 'Shield';
      case 'status_change':
        return 'RefreshCw';
      case 'data_refresh':
        return 'RefreshCcw';
      default:
        return 'Activity';
    }
  };

  const getEntityIcon = (entity) => {
    switch (entity) {
      case 'user':
        return 'User';
      case 'screen':
        return 'Monitor';
      case 'feature':
        return 'Zap';
      case 'permission':
        return 'Lock';
      case 'system':
        return 'Server';
      case 'data':
        return 'Database';
      case 'report':
        return 'FileText';
      case 'session':
        return 'Clock';
      default:
        return 'Box';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatActionType = (action) => {
    return action?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  const formatEntityType = (entity) => {
    return entity?.charAt(0)?.toUpperCase() + entity?.slice(1);
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
  };

  const handleCloseDetails = () => {
    setSelectedLog(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header with Export Buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Audit Log
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Track all user actions and system changes
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="FileDown"
            onClick={handleExportCSV}
          >
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="FileText"
            onClick={handleExportPDF}
          >
            PDF
          </Button>
        </div>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="relative lg:col-span-2">
          <Icon
            name="Search"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
            className="w-full pl-10 pr-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          />
        </div>
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e?.target?.value)}
          className="px-3 md:px-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
        >
          <option value="all">All Actions</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="login">Login</option>
          <option value="logout">Logout</option>
          <option value="export">Export</option>
          <option value="view">View</option>
          <option value="permission_change">Permission Change</option>
          <option value="status_change">Status Change</option>
          <option value="data_refresh">Data Refresh</option>
        </select>
        <select
          value={filterEntity}
          onChange={(e) => setFilterEntity(e?.target?.value)}
          className="px-3 md:px-4 py-2 md:py-2.5 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
        >
          <option value="all">All Entities</option>
          <option value="user">User</option>
          <option value="screen">Screen</option>
          <option value="feature">Feature</option>
          <option value="permission">Permission</option>
          <option value="system">System</option>
          <option value="data">Data</option>
          <option value="report">Report</option>
          <option value="session">Session</option>
        </select>
        <Button
          variant="outline"
          iconName={sortOrder === 'desc' ? 'ArrowDown' : 'ArrowUp'}
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
        >
          Sort
        </Button>
      </div>
      {/* Date Range Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-muted-foreground mb-1.5">
            Start Date
          </label>
          <input
            type="date"
            value={dateRange?.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e?.target?.value })}
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1.5">
            End Date
          </label>
          <input
            type="date"
            value={dateRange?.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e?.target?.value })}
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
          />
        </div>
      </div>
      {/* Audit Logs List */}
      <div className="space-y-3 md:space-y-4">
        {filteredLogs?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileSearch" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No audit logs found</p>
          </div>
        ) : (
          filteredLogs?.map((log) => (
            <div
              key={log?.id}
              className="bg-card border border-border rounded-md p-4 md:p-6 hover:shadow-md transition-smooth cursor-pointer"
              onClick={() => handleViewDetails(log)}
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <div
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium font-caption ${getActionColor(
                        log?.actionType
                      )}`}
                    >
                      <Icon name={getActionIcon(log?.actionType)} size={12} />
                      <span>{formatActionType(log?.actionType)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium font-caption bg-muted text-muted-foreground">
                      <Icon name={getEntityIcon(log?.entityType)} size={12} />
                      <span>{formatEntityType(log?.entityType)}</span>
                    </div>
                  </div>

                  <p className="text-sm md:text-base text-foreground mb-2 font-medium">
                    {log?.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">
                        User
                      </p>
                      <p className="text-sm font-medium text-foreground font-data">
                        {log?.userName || 'Unknown'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {log?.userEmail || 'N/A'}
                      </p>
                    </div>
                    {log?.entityName && (
                      <div>
                        <p className="text-xs text-muted-foreground font-caption mb-1">
                          Entity
                        </p>
                        <p className="text-sm font-medium text-foreground font-data">
                          {log?.entityName}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground font-caption mb-1">
                        Timestamp
                      </p>
                      <p className="text-sm font-medium text-foreground font-data">
                        {formatTimestamp(log?.timestamp)}
                      </p>
                    </div>
                    {log?.ipAddress && (
                      <div>
                        <p className="text-xs text-muted-foreground font-caption mb-1">
                          IP Address
                        </p>
                        <p className="text-sm font-medium text-foreground font-data">
                          {log?.ipAddress}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ChevronRight"
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleViewDetails(log);
                    }}
                  >
                    Details
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Detail Modal */}
      {selectedLog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={handleCloseDetails}
        >
          <div
            className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Audit Log Details
              </h3>
              <button
                onClick={handleCloseDetails}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-4 md:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">
                    Action Type
                  </p>
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium font-caption ${getActionColor(
                      selectedLog?.actionType
                    )}`}
                  >
                    <Icon name={getActionIcon(selectedLog?.actionType)} size={12} />
                    <span>{formatActionType(selectedLog?.actionType)}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">
                    Entity Type
                  </p>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium font-caption bg-muted text-muted-foreground">
                    <Icon name={getEntityIcon(selectedLog?.entityType)} size={12} />
                    <span>{formatEntityType(selectedLog?.entityType)}</span>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs text-muted-foreground font-caption mb-1">
                  Description
                </p>
                <p className="text-sm text-foreground">{selectedLog?.description}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">
                    User
                  </p>
                  <p className="text-sm font-medium text-foreground">
                    {selectedLog?.userName || 'Unknown'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedLog?.userEmail || 'N/A'}
                  </p>
                </div>
                {selectedLog?.entityName && (
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">
                      Entity Name
                    </p>
                    <p className="text-sm font-medium text-foreground">
                      {selectedLog?.entityName}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">
                    Timestamp
                  </p>
                  <p className="text-sm font-medium text-foreground">
                    {formatTimestamp(selectedLog?.timestamp)}
                  </p>
                </div>
                {selectedLog?.ipAddress && (
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">
                      IP Address
                    </p>
                    <p className="text-sm font-medium text-foreground">
                      {selectedLog?.ipAddress}
                    </p>
                  </div>
                )}
              </div>

              {selectedLog?.userAgent && (
                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">
                    User Agent
                  </p>
                  <p className="text-sm text-foreground break-all">
                    {selectedLog?.userAgent}
                  </p>
                </div>
              )}

              {selectedLog?.changesBefore && (
                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">
                    Changes Before
                  </p>
                  <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto">
                    {JSON.stringify(selectedLog?.changesBefore, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog?.changesAfter && (
                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">
                    Changes After
                  </p>
                  <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto">
                    {JSON.stringify(selectedLog?.changesAfter, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
